import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import hybridBlogService from "../../services/hybridBlogService";
import { useTranslation } from "../../hooks/useTranslation";
import { MEDIA_URL } from "../../config/constants";

const RelatedPosts = ({ currentPost }) => {
  const navigate = useNavigate();
  const { getLocalizedPath } = useTranslation();
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadRelatedPosts = async () => {
      if (!currentPost) return;

      try {
        setLoading(true);
        const posts = await hybridBlogService.fetchRelatedBlogs(
          currentPost.slug || currentPost.id,
          currentPost.category,
          currentPost.tags || [],
          3
        );
        setRelatedPosts(posts);
      } catch (error) {
        console.error('Error loading related posts:', error);
        // Fallback to empty array to avoid UI issues
        setRelatedPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadRelatedPosts();
  }, [currentPost]);

  // Fallback data if API fails or while loading
  const fallbackPosts = [
    {
      id: "2",
      slug: "essential-packing-list-portugal",
      title: "Essential Packing List for Your Portuguese Campervan Adventure",
      excerpt:
        "Everything you need to pack for a comfortable and safe campervan journey through Portugal.",
      image:
        `${MEDIA_URL}/images/blog/blog-placeholder.webp`,
      readTime: "6 Min Read",
      date: "July 20, 2024",
      category: "Packing Tips",
    },
    {
      id: "3",
      slug: "best-coastal-routes-portugal",
      title: "Best Coastal Routes for Campervan Travel in Portugal",
      excerpt:
        "Discover the most scenic coastal drives and hidden beaches perfect for campervan stops.",
      image:
        `${MEDIA_URL}/images/blog/blog-placeholder.webp`,
      readTime: "10 Min Read",
      date: "July 18, 2024",
      category: "Routes",
    },
    {
      id: "4",
      slug: "sustainable-campervan-travel-portugal",
      title: "Sustainable Campervan Travel: Leave No Trace in Portugal",
      excerpt:
        "How to travel responsibly and protect Portugal's beautiful natural environments.",
      image:
        `${MEDIA_URL}/images/blog/blog-placeholder.webp`,
      readTime: "5 Min Read",
      date: "July 15, 2024",
      category: "Sustainability",
    },
  ];

  const postsToDisplay = loading ? fallbackPosts : (relatedPosts.length > 0 ? relatedPosts : fallbackPosts);

  const handlePostClick = (slug) => {
    navigate(getLocalizedPath(`/blog/${slug}`));
  };

  const handleViewAllClick = () => {
    navigate(getLocalizedPath('/blog'));
  };

  return (
    <div className="related-posts-section">
      <div className="related-posts-container">
        <div className="related-posts-header">
          <h2 className="related-posts-title">Related Articles</h2>
          <p className="related-posts-description">
            Continue your campervan journey with these helpful guides and tips
          </p>
        </div>

        <div className="related-posts-grid">
          {postsToDisplay.map((post) => (
            <article
              key={post.slug || post.id}
              className="related-post-card"
              onClick={() => handlePostClick(post.slug || post.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="related-post-image">
                <img src={post.image} alt={post.title} />
                <div className="related-post-category-badge">
                  {post.category}
                </div>
              </div>

              <div className="related-post-content">
                <h3 className="related-post-title">{post.title}</h3>
                <p className="related-post-description">{post.excerpt || post.description}</p>

                <div className="related-post-meta">
                  <span className="related-post-date">
                    {post.date ? hybridBlogService.formatDate(post.date) : post.date}
                  </span>
                  <span className="meta-separator">•</span>
                  <span className="related-post-read-time">
                    {post.readTime}
                  </span>
                </div>

                <button
                  className="read-more-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePostClick(post.slug || post.id);
                  }}
                >
                  <span>Read Article</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 12L10 8L6 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="view-all-posts">
          <button
            className="view-all-button"
            onClick={handleViewAllClick}
          >
            View All Blog Posts
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 15L12.5 10L7.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelatedPosts;
